import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import Chat from './components/Chat';
import Evaluate from './components/Evaluate';
import './App.css';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { loadConfig } from './config';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [userPool, setUserPool] = useState(null);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mode, setMode] = useState('chat'); // 'chat' or 'evaluate'
  const [models, setModels] = useState([]);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [endpoints, setEndpoints] = useState([]);

  const fetchModels = async (apiUrl, token) => {
    try {
      const response = await fetch(`${apiUrl}models`, {
        headers: { 'Authorization': token },
      });
      if (response.ok) {
        const data = await response.json();
        setModels(data.models || []);
      }
    } catch (err) {
      console.error('Error fetching models:', err);
    } finally {
      setModelsLoaded(true);
    }
  };

  const fetchEndpoints = async (apiUrl, token) => {
    try {
      const response = await fetch(`${apiUrl}endpoints`, {
        headers: { 'Authorization': token },
      });
      if (response.ok) {
        const data = await response.json();
        setEndpoints(data.endpoints || []);
      }
    } catch (err) {
      console.error('Error fetching endpoints:', err);
    }
  };

  useEffect(() => {
    // Load configuration and initialize Cognito
    const initializeApp = async () => {
      try {
        const loadedConfig = await loadConfig();
        setConfig(loadedConfig);

        const poolData = {
          UserPoolId: loadedConfig.userPoolId,
          ClientId: loadedConfig.userPoolClientId,
        };

        const pool = new CognitoUserPool(poolData);
        setUserPool(pool);

        // Check if user is already authenticated
        const currentUser = pool.getCurrentUser();
        if (currentUser) {
          // Timeout so a hung getSession call doesn't block the app forever
          const sessionTimeout = setTimeout(() => {
            console.warn('Session check timed out — showing login');
            setIsLoading(false);
          }, 5000);

          try {
            currentUser.getSession(async (err, session) => {
              clearTimeout(sessionTimeout);
              if (err) {
                console.error('Session error:', err);
                setIsLoading(false);
                return;
              }
              if (session && session.isValid()) {
                const token = session.getIdToken().getJwtToken();
                setIsAuthenticated(true);
                setCognitoUser(currentUser);
                setIdToken(token);
                // Pre-fetch models before showing the UI
                await fetchModels(loadedConfig.apiUrl, token);
                await fetchEndpoints(loadedConfig.apiUrl, token);
              }
              setIsLoading(false);
            });
          } catch (err) {
            clearTimeout(sessionTimeout);
            console.error('getSession threw:', err);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  const handleLogin = async (user, token) => {
    setIsAuthenticated(true);
    setCognitoUser(user);
    setIdToken(token);
    // Fetch models immediately on login before rendering chat
    if (config) {
      await fetchModels(config.apiUrl, token);
      await fetchEndpoints(config.apiUrl, token);
    }
  };

  const handleLogout = () => {
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    setIsAuthenticated(false);
    setCognitoUser(null);
    setIdToken(null);
    setMode('chat');
    setModels([]);
    setEndpoints([]);
    setModelsLoaded(false);
  };

  if (isLoading || (isAuthenticated && !modelsLoaded)) {
    return (
      <div className="App">
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontSize: '18px',
        }}>
          {isAuthenticated && !modelsLoaded ? 'Loading models...' : 'Loading configuration...'}
        </div>
      </div>
    );
  }

  if (!userPool || !config) {
    return (
      <div className="App">
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px',
        }}>
          Failed to load configuration. Please check your deployment.
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {!isAuthenticated ? (
        <Login onLogin={handleLogin} userPool={userPool} />
      ) : mode === 'evaluate' ? (
        <Evaluate
          idToken={idToken}
          config={config}
          models={models}
          onBack={() => setMode('chat')}
        />
      ) : (
        <Chat
          onLogout={handleLogout}
          idToken={idToken}
          config={config}
          models={models}
          endpoints={endpoints}
          onEvaluate={() => setMode('evaluate')}
        />
      )}
    </div>
  );
}

export default App;
